import '../GraphSidePanel.css';
import type { NetworkViewerTabId } from 'components/Graph/GraphSidePanelData';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';

import {
  type Filters,
  type GraphPath,
  IPF_NETWORK_VIEWER,
  IPF_NO_VALUE_CLOUD,
  type Layout,
  assertType,
  isNonNullable,
  isTopologyInitialParameters,
  pick,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphPathWarning } from 'components/Graph/GraphPathWarning/GraphPathWarning';
import { isTopologyGraphMetadata } from 'components/Graph/GraphState';
import { saveNetworkViewerGroupBy } from 'components/Graph/commands/saveNetworkViewerGroupBy';
import { submitTopologyInput } from 'components/Graph/commands/topology/submitTopologyInput';
import { getPathNameLabel } from 'components/Graph/helpers/getPathNameLabel';
import { loadAttributesSummaryTable } from 'components/Graph/helpers/loadAttributesSummaryTable';
import { selectPathWarnings } from 'components/Graph/model/selectors/graphMetadata/selectPathWarnings';
import { selectTopologyPathsWithoutNetworkViewer } from 'components/Graph/model/selectors/graphMetadata/selectTopologyPathsWithoutNetworkViewer';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { pipe } from 'fp-ts/lib/function';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';
import {
  type InfiniteScrollItemsHook,
  type InfiniteScrollItemsResult,
  useInfiniteScrollItems,
} from 'utils/react/useInfiniteScrollItems';
import { useApi } from 'utils/request/useApi';
import type {
  GraphNetworkViewerDataInputFormValues,
  PathOptionType,
} from './GraphNetworkViewerDataInputForm';
import { getPathNameIcon } from './getPathNameIcon';
import { getPathNameLabelStyle } from './getPathNameLabelStyle';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { loadPathLayout } from 'components/Graph/commands/loadPathLayout';
import { setPathLayouts } from 'components/Graph/commands/setPathLayouts';
import { compareCaseInsensitive } from 'utils/compareCaseInsensitive';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useTraceableDeps } from 'utils/hooks/useTraceableDeps';

const PATHS_PAGE_SIZE = 20;

export interface NetworkViewerDataInputHook {
  formik: FormikProps<GraphNetworkViewerDataInputFormValues>;
  loadingState: LoadingState;
  pathsInfiniteScroll: InfiniteScrollItemsHook<PathOptionType>;
  setPathFilter: (filter: string) => void;
  pathLayoutEdit: GraphPath | null;
  setPathLayoutEdit: (path: GraphPath | null) => void;
}

export const useNetworkViewerDataInput = (): NetworkViewerDataInputHook => {
  const [loadingState, setLoadingState] = useLoadingState();
  const msg = useLocale();
  const apiRequest = useApi().request;
  const ctxProps = useGraphContextSelectors({
    attributeFilters: prop('attributeFilters'),
    dispatch: prop('dispatch'),
    initialParameters: prop('initialParameters'),
    savedGroupBy: prop('networkViewer', 'groupBy'),
    topologyMetadata: (s) =>
      isTopologyGraphMetadata(s.graphMetadata) ? s.graphMetadata : null,
    loading: prop('loading'),
    pathLayouts: prop('pathLayouts'),
    pathsWithoutNetworkViewer: selectTopologyPathsWithoutNetworkViewer,
    pathWarnings: selectPathWarnings,
    snapshotId: prop('snapshotId'),
  });
  const [pathFilter, setPathFilter] = useState<string>('');
  const [pathLayoutEdit, setPathLayoutEdit] = useState<GraphPath | null>(null);

  const initialValuesDeps = useTraceableDeps(
    pick(['initialParameters', 'topologyMetadata'])(ctxProps),
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: initial values
  const initialValues: GraphNetworkViewerDataInputFormValues = useMemo(() => {
    const { groupBy, paths } =
      // when the `initialParameters` is changed, it has the highest priority
      // because the `topologyMetadata` updates after the `initialParameters`
      // and can overwrite the actual data
      isTopologyInitialParameters(ctxProps.initialParameters) &&
      initialValuesDeps.prev?.initialParameters !== ctxProps.initialParameters
        ? ctxProps.initialParameters
        : // otherwise, use current parameters from topology metadata if it's available
          (ctxProps.topologyMetadata?.parameters ?? {
            // else use the last known `groupBy`
            groupBy: ctxProps.savedGroupBy,
            paths: [],
          });

    return {
      groupBy,
      paths: new Set<GraphPath>(paths),
      pathFilter: '',
    };
  }, [initialValuesDeps.version]);

  const [invalidTab, setInvalidTab] = useState<NetworkViewerTabId | null>(null);

  const formik = useFormik<GraphNetworkViewerDataInputFormValues>({
    initialValues,
    validate: (values) => {
      const errors: FormikErrors<GraphNetworkViewerDataInputFormValues> = {};
      setInvalidTab(null);
      if (values.groupBy === null) {
        errors.groupBy =
          msg.canvasGraph.graphSidePanel.networkViewerDataInput.groupBy.required;
        setInvalidTab('dataInput');
      } else if (values.paths.size === 0) {
        // formik wrongly types Set<> error fields as objects
        (errors.paths as string) =
          msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList.required;
        setInvalidTab('dataInput');
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit(async ({ groupBy, paths }) => {
      assertType(isNonNullable, groupBy); // covered by validation, just elevate type here
      await ctxProps.dispatch(submitTopologyInput, { groupBy, paths });
    }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: to be triggered on submit
  useEffect(() => {
    if (invalidTab !== null) {
      ctxProps.dispatch(showSidePanel, { networkViewerTabId: invalidTab });
      // shouldn't be needed as effect is triggered only by submit count change -> after fresh
      // validation, but let's rather be defensive there.
      setInvalidTab(null);
    }
  }, [formik.submitCount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(formik.setValues): stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(formik.setTouched): stable
  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.dispatch): stable
  useEffect(() => {
    formik.setValues(initialValues);
    formik.setTouched({});
    ctxProps.dispatch(saveNetworkViewerGroupBy, {
      groupBy: initialValues.groupBy,
    });
  }, [initialValues]);

  /**
   * Paths loaded from graphs can have more icons than paths from summary table.
   * So hoisted paths need their own function to show path label.
   */
  const getHoistedPathLabel = useStableCallback(
    (path: GraphPath): ReactNode => (
      <>
        {pipe(getPathNameLabel(path), getPathNameLabelStyle(path))}
        {getPathNameIcon(path)}
        {ctxProps.pathWarnings.missingPaths.has(path) && (
          <GraphPathWarning type='missing' />
        )}
        {ctxProps.pathWarnings.emptyPaths.has(path) && (
          <GraphPathWarning type='empty' />
        )}
      </>
    ),
  );

  const loadPaths = async (
    start: number,
  ): Promise<InfiniteScrollItemsResult<PathOptionType>> => {
    if (formik.values.groupBy === null) {
      return {
        items: [],
        totalCount: 0,
      };
    }

    const filters: Filters = {
      name: ['eq', formik.values.groupBy],
      ...(ctxProps.pathsWithoutNetworkViewer.length > 0 &&
        ctxProps.topologyMetadata?.parameters.groupBy ===
          formik.values.groupBy && {
          and: ctxProps.pathsWithoutNetworkViewer.map((path) => ({
            path: ['neq', path],
          })),
        }),
      ...(pathFilter ? { path: ['like', pathFilter.trim()] } : {}),
    };

    const loadAttributesSummary = () =>
      loadAttributesSummaryTable<{
        label: string;
        path: GraphPath;
        layout: Layout;
      }>(
        apiRequest,
        {
          pagination: { start, limit: PATHS_PAGE_SIZE },
          filters,
          columns: ['label', 'layout', 'path'],
          snapshotId: ctxProps.snapshotId,
        },
        ctxProps.attributeFilters,
      );

    const ensureLoadedNetworkViewerLayout = async () => {
      if (
        formik.values.groupBy &&
        !ctxProps.pathLayouts[formik.values.groupBy][IPF_NETWORK_VIEWER]
      ) {
        await ctxProps.dispatch(loadPathLayout, {
          groupBy: formik.values.groupBy,
          paths: [IPF_NETWORK_VIEWER],
        });
      }
    };

    const [pathsResult] = await withLoadingIndicator(setLoadingState)(() =>
      Promise.all([loadAttributesSummary(), ensureLoadedNetworkViewerLayout()]),
    );

    ctxProps.dispatch(setPathLayouts, {
      groupBy: formik.values.groupBy,
      options: pathsResult.data.map((result) => ({
        path: result.path,
        layout: result.layout,
      })),
    });

    return {
      items: pathsResult.data.map(({ label, path }) => ({
        label: (
          <>
            {getPathNameLabelStyle(path)(label)}
            {getPathNameIcon(path)}
          </>
        ),
        value: path,
      })),
      totalCount: pathsResult._meta.count,
    };
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.pathWarnings): used in getHoistedPathLabel
  const hoistedPaths = useMemo(() => {
    const matchFilter = compareCaseInsensitive(pathFilter);
    return ctxProps.topologyMetadata?.parameters.groupBy ===
      formik.values.groupBy
      ? ctxProps.pathsWithoutNetworkViewer
          .filter(matchFilter)
          // Show '<NO VALUE>' as first item
          .sort((a, b) => {
            if (a === IPF_NO_VALUE_CLOUD) {
              return -1;
            }
            if (b === IPF_NO_VALUE_CLOUD) {
              return 1;
            }
            return 0;
          })
          .map((path) => ({ label: getHoistedPathLabel(path), value: path }))
      : [];
  }, [
    ctxProps.topologyMetadata?.parameters.groupBy,
    formik.values.groupBy,
    ctxProps.pathsWithoutNetworkViewer,
    pathFilter,
    ctxProps.pathWarnings,
  ]);

  const pathsInfiniteScroll = useInfiniteScrollItems({
    getItemKey: prop('value'),
    hoistedItems: hoistedPaths,
    loadItems: loadPaths,
    reloadDeps: [
      formik.values.groupBy,
      pathFilter,
      ctxProps.snapshotId,
      ctxProps.attributeFilters,
    ],
  });

  const debounceFilterPaths = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);
  useEffectOnChange(() => {
    debounceFilterPaths(() => {
      // we should not debounce reloading first page immediately, but rather
      // just debounced set the filter value to state.
      // memoed hoistedPaths will collect the change and pathsInfiniteScroll
      // may trigger loadFirstPage based on that action itself. In that case,
      // it returns noop version of loadFirstPage for that render, so useEffect
      // listening to pathFilter state field here will not cause duplicate request.
      setPathFilter(formik.values.pathFilter);
    });
  }, [formik.values.pathFilter]);

  return {
    formik,
    loadingState,
    pathsInfiniteScroll,
    setPathFilter,
    pathLayoutEdit,
    setPathLayoutEdit,
  };
};
